// aluno/lgpd-pages.js — Páginas estáticas /termo e /privacidade.
//
// Renderiza os documentos LGPD a partir dos .md da pasta docs/lgpd/.
// Usamos Vite ?raw para importar o conteúdo markdown como string em build-time:
// uma única fonte de verdade (os .md continuam canônicos).
//
// Conversor Markdown minimalista — sem dependência externa.
// Suporta: headings h1-h4, parágrafos, listas (- e *), listas numeradas,
// negrito (**), itálico (*), código inline (`), links [txt](url), tabelas
// simples estilo GFM, citações (>), separador ---, parágrafos vazios.
//
// Contrato público:
//   renderTermoPage(container)       — H3
//   renderPrivacidadePage(container) — H3
//   renderConsentimentosPage(container) — H5 (gerenciar + revogar granular)

import termoMd       from '../../docs/lgpd/01-termo-consentimento.md?raw';
import privacidadeMd from '../../docs/lgpd/02-politica-privacidade.md?raw';
import { auth } from '../core/auth.js';
import { api } from '../core/api.js';
import { renderExportSection, bindExportEvents } from './lgpd-export.js';
import {
  loadConsentSnapshot,
  saveConsentSnapshot,
  getConsentTermoVersion,
  getFinalidades,
} from './consent-modal.js';
import { trackLgpdRevoke } from '../core/analytics.js';

// ── Renderers públicos ───────────────────────────────────────────────────────

export function renderTermoPage(container) {
  _renderDocumentPage(container, {
    titulo: 'Termo de Consentimento',
    markdown: termoMd,
    backTo: _voltarHref(),
  });
}

export function renderPrivacidadePage(container) {
  _renderDocumentPage(container, {
    titulo: 'Política de Privacidade',
    markdown: privacidadeMd,
    backTo: _voltarHref(),
  });
}

/**
 * H5 — Tela de gerenciamento de consentimentos.
 *
 * Mostra as 5 finalidades com toggles. As 2 obrigatórias (execução do curso
 * e registro pedagógico) são travadas pois a base legal é execução de contrato
 * (Art. 7º V) — não podem ser revogadas sem cancelar a matrícula.
 * As 3 opcionais (marketing, imagem/voz, depoimentos) podem ser alternadas
 * livremente e a mudança persiste local + backend (best-effort).
 *
 * Se não houver snapshot local, mostra estado vazio convidando a fazer login.
 */
export function renderConsentimentosPage(container) {
  const snapshot = loadConsentSnapshot();
  const version = getConsentTermoVersion();
  const back = _voltarHref();
  const logged = auth.isLoggedIn();

  let body;
  if (!snapshot) {
    body = `
      <div class="lgpd-page-empty">
        <p>Você ainda não registrou consentimentos nesta sessão.</p>
        <p class="lgpd-page-hint">
          Quando você faz login, as autorizações que marcou aparecem aqui
          e podem ser <strong>revogadas individualmente</strong> a qualquer momento.
        </p>
        ${logged ? '' : '<p><a class="lgpd-page-cta" href="#/login">Entrar com Google</a></p>'}
      </div>
    `;
  } else {
    body = _renderGerenciarBody(snapshot, version);
  }

  container.innerHTML = `
    <article class="lgpd-page">
      <header class="lgpd-page-head">
        <a href="${back}" class="lgpd-page-back">← Voltar</a>
        <h1 class="lgpd-page-title">Meus consentimentos</h1>
      </header>
      ${body}
    </article>
  `;

  if (snapshot) _bindGerenciarEvents(container, snapshot);
}

function _renderGerenciarBody(snapshot, version) {
  const dt = new Date(snapshot.timestamp).toLocaleString('pt-BR');
  const choices = snapshot.choices || {};
  const finalidades = getFinalidades();

  const items = finalidades.map((f) => {
    const on = !!choices[f.id];
    const disabledAttr = f.obrigatoria ? 'disabled aria-disabled="true"' : '';
    const checkedAttr = on ? 'checked' : '';
    const badge = f.obrigatoria
      ? '<span class="lgpd-consent-badge is-required">Obrigatório</span>'
      : '<span class="lgpd-consent-badge is-optional">Opcional</span>';

    return `
      <li class="lgpd-consent-row ${on ? 'is-on' : 'is-off'}" data-finalidade="${f.id}">
        <div class="lgpd-consent-meta">
          <div class="lgpd-consent-head">
            <span class="lgpd-consent-name">${_esc(f.titulo)}</span>
            ${badge}
          </div>
          <p class="lgpd-consent-desc">${_esc(f.descricao)}</p>
          <p class="lgpd-consent-base">${_esc(f.base)}</p>
        </div>
        <label class="lgpd-switch" for="lgpd-sw-${f.id}" title="${f.obrigatoria ? 'Não pode ser revogado — necessário para execução do contrato' : (on ? 'Clique para revogar' : 'Clique para autorizar')}">
          <input
            type="checkbox"
            id="lgpd-sw-${f.id}"
            class="lgpd-switch-input"
            data-finalidade="${f.id}"
            data-obrigatoria="${f.obrigatoria}"
            ${checkedAttr}
            ${disabledAttr}
          />
          <span class="lgpd-switch-track" aria-hidden="true">
            <span class="lgpd-switch-thumb"></span>
          </span>
          <span class="lgpd-switch-label">${on ? 'Autorizado' : 'Não autorizado'}</span>
        </label>
      </li>
    `;
  }).join('');

  return `
    <div class="lgpd-page-consents">
      <p class="lgpd-page-hint">
        Última alteração: <strong>${dt}</strong> · Termo v${version}
      </p>
      <div class="lgpd-consent-status-banner" role="status" aria-live="polite" data-status hidden></div>
      <ul class="lgpd-consent-list">${items}</ul>
      <p class="lgpd-page-hint">
        As autorizações obrigatórias (execução do curso e registro pedagógico) não podem ser revogadas
        sem cancelar a matrícula. Para isso, fale com
        <a href="mailto:contato@eloquence.com.br">contato@eloquence.com.br</a>.
      </p>

      ${renderExportSection()}
    </div>
  `;
}

function _bindGerenciarEvents(container, snapshot) {
  const statusEl = container.querySelector('[data-status]');

  bindExportEvents(container, _mostrarStatus);

  container.querySelectorAll('.lgpd-switch-input').forEach((input) => {
    input.addEventListener('change', async (ev) => {
      const id = input.dataset.finalidade;
      const obrigatoria = input.dataset.obrigatoria === 'true';
      if (obrigatoria) {
        ev.preventDefault();
        input.checked = true;
        _mostrarStatus(statusEl, 'Essa autorização é obrigatória para o curso — não pode ser revogada por aqui.', 'warn');
        return;
      }

      const novoValor = input.checked;
      const choicesAntes = { ...(snapshot.choices || {}) };
      const choicesDepois = { ...choicesAntes, [id]: novoValor };

      // Atualiza UI (linha) otimisticamente
      const row = input.closest('.lgpd-consent-row');
      if (row) {
        row.classList.toggle('is-on', novoValor);
        row.classList.toggle('is-off', !novoValor);
      }
      const label = input.closest('.lgpd-switch')?.querySelector('.lgpd-switch-label');
      if (label) label.textContent = novoValor ? 'Autorizado' : 'Não autorizado';

      // Persiste local imediatamente
      const novoSnap = saveConsentSnapshot(choicesDepois);
      snapshot.choices = novoSnap.choices;
      snapshot.timestamp = novoSnap.timestamp;

      // Analytics: evento semântico (Consent Mode já foi atualizado via evento global).
      trackLgpdRevoke(id, novoValor);
      _mostrarStatus(statusEl, 'Salvando alteração...', 'info');
      input.disabled = true;

      // Backend best-effort
      const current = auth.getCurrent?.();
      const idToken = current?.idToken;
      if (!idToken) {
        _mostrarStatus(statusEl, 'Alteração salva localmente. Faça login para sincronizar com o servidor.', 'warn');
        input.disabled = false;
        return;
      }
      const result = await api.revogarConsentimento({
        idToken,
        version: snapshot.version,
        choices: choicesDepois,
        uaHash: _uaHashCurtoLocal(),
      });
      input.disabled = false;
      if (result.ok) {
        _mostrarStatus(
          statusEl,
          novoValor
            ? 'Autorização atualizada com sucesso.'
            : 'Autorização revogada. Registramos sua decisão.',
          'ok'
        );
      } else {
        _mostrarStatus(
          statusEl,
          'Alteração salva localmente, mas o servidor não respondeu. Tentaremos de novo no próximo login.',
          'warn'
        );
      }
    });
  });
}

function _mostrarStatus(el, msg, kind) {
  if (!el) return;
  el.hidden = false;
  el.textContent = msg;
  el.className = `lgpd-consent-status-banner is-${kind || 'info'}`;
}

function _uaHashCurtoLocal() {
  const ua = (navigator?.userAgent || '').slice(0, 200);
  let h = 0;
  for (let i = 0; i < ua.length; i++) {
    h = ((h << 5) - h + ua.charCodeAt(i)) | 0;
  }
  return 'ua_' + (h >>> 0).toString(36);
}

// ── Render genérico de documento ─────────────────────────────────────────────

function _renderDocumentPage(container, { titulo, markdown, backTo }) {
  const html = mdToHtml(markdown);
  container.innerHTML = `
    <article class="lgpd-page">
      <header class="lgpd-page-head">
        <a href="${backTo}" class="lgpd-page-back">← Voltar</a>
        <h1 class="lgpd-page-title">${_esc(titulo)}</h1>
      </header>
      <div class="lgpd-page-body">${html}</div>
    </article>
  `;
}

function _voltarHref() {
  // Se autenticado, volta para a home correspondente; senão, para login.
  if (auth.isLoggedIn()) {
    return auth.isAdmin() ? '#/admin' : '#/aluno';
  }
  return '#/login';
}

// ── Conversor Markdown → HTML (mínimo, seguro) ───────────────────────────────

export function mdToHtml(md) {
  if (!md) return '';
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Separador horizontal
    if (/^\s*---+\s*$/.test(line)) {
      out.push('<hr>');
      i++;
      continue;
    }

    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const lvl = h[1].length;
      out.push(`<h${lvl}>${_inline(h[2])}</h${lvl}>`);
      i++;
      continue;
    }

    // Tabela GFM: header | --- | row
    if (/^\s*\|.*\|\s*$/.test(line) && /^\s*\|[-\s|:]+\|\s*$/.test(lines[i + 1] || '')) {
      const headerCells = _splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(_splitRow(lines[i]));
        i++;
      }
      const thead = `<thead><tr>${headerCells.map(c => `<th>${_inline(c)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rows.map(r =>
        `<tr>${r.map(c => `<td>${_inline(c)}</td>`).join('')}</tr>`
      ).join('')}</tbody>`;
      out.push(`<table class="lgpd-md-table">${thead}${tbody}</table>`);
      continue;
    }

    // Citação (>)
    if (/^\s*>\s?/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${_inline(quoteLines.join(' '))}</blockquote>`);
      continue;
    }

    // Lista não-ordenada (- ou *)
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      out.push(`<ul>${items.map(it => `<li>${_inline(it)}</li>`).join('')}</ul>`);
      continue;
    }

    // Lista ordenada (1. 2. 3.)
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      out.push(`<ol>${items.map(it => `<li>${_inline(it)}</li>`).join('')}</ol>`);
      continue;
    }

    // Linha em branco
    if (/^\s*$/.test(line)) {
      i++;
      continue;
    }

    // Parágrafo (junta linhas consecutivas até em branco ou bloco)
    const para = [line];
    i++;
    while (i < lines.length
          && !/^\s*$/.test(lines[i])
          && !/^(#{1,4})\s+/.test(lines[i])
          && !/^\s*[-*]\s+/.test(lines[i])
          && !/^\s*\d+\.\s+/.test(lines[i])
          && !/^\s*>\s?/.test(lines[i])
          && !/^\s*---+\s*$/.test(lines[i])
          && !/^\s*\|.*\|\s*$/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    out.push(`<p>${_inline(para.join(' '))}</p>`);
  }

  return out.join('\n');
}

function _splitRow(line) {
  // Remove pipes de borda e divide
  return line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(s => s.trim());
}

// ── Inline formatação (escape HTML primeiro, depois aplica marcação) ────────

function _inline(text) {
  let s = _esc(text);
  // Código inline: `...`
  s = s.replace(/`([^`]+)`/g, (_, g) => `<code>${g}</code>`);
  // Negrito: **...**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Itálico: *...*  (após negrito pra não colidir)
  s = s.replace(/(^|[^\w*])\*([^*\n]+)\*(?=[^\w*]|$)/g, '$1<em>$2</em>');
  // Links [texto](url) — url pode ser http(s), mailto, #/...
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, txt, url) => {
    const safeUrl = /^(https?:|mailto:|#\/)/.test(url) ? url : '#';
    const ext = /^https?:/.test(url) ? ' target="_blank" rel="noopener"' : '';
    return `<a href="${safeUrl}"${ext}>${txt}</a>`;
  });
  return s;
}

function _esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
